// Story 3.7 — shared media-query primitive for the SPA.
//
// FIRST matchMedia consumer across apps/web/src/** — establishes the
// `useSyncExternalStore`-based pattern so the two Story-3.7 hooks
// (useEffectiveMotionMode + useMobileFidelity) compose against one
// canonical implementation. Future ResponsiveImage / collapsing-nav /
// theme-aware components reuse this.
//
// React's useSyncExternalStore is the concurrent-mode-safe pattern for
// external sources of truth (vs the old useEffect+useState dance that
// can tear under transitions). Subscribes to MediaQueryList `change`
// events (the standard addEventListener API, NOT the deprecated
// MediaQueryList.addListener variant).

import { useCallback, useSyncExternalStore } from "react";

function getServerSnapshot(): boolean {
  // SSR-safe sentinel. The SPA never SSRs but the hook contract should
  // be honest about the no-window case. Consumers (e.g. tests under
  // jsdom without the matchMedia polyfill) also land here.
  return false;
}

/** Reactive boolean for a CSS media query.
 *
 *  Subscribes to MediaQueryList `change` events + cleans up on unmount.
 *  Returns false during the brief window before first browser paint if
 *  `window.matchMedia` is unavailable (e.g. jsdom without polyfill).
 *
 *  Used by Story 3.7's `useEffectiveMotionMode` (for
 *  `(prefers-reduced-motion: reduce)`) + `useMobileFidelity` (for
 *  `(max-width: 768px)`). */
export function useMatchMedia(query: string): boolean {
  const subscribe = useCallback(
    (callback: () => void) => {
      if (
        typeof window === "undefined" ||
        typeof window.matchMedia !== "function"
      ) {
        return () => {};
      }
      const mql = window.matchMedia(query);
      mql.addEventListener("change", callback);
      return () => mql.removeEventListener("change", callback);
    },
    [query],
  );

  const getSnapshot = useCallback(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return false;
    }
    return window.matchMedia(query).matches;
  }, [query]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
